import { ChangeDetectionStrategy, Component, computed, inject, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  MAT_DIALOG_DATA,
  MatDialogModule,
  MatDialogRef,
} from '@angular/material/dialog';
import { MatButtonModule } from '@angular/material/button';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatInputModule } from '@angular/material/input';
import { MatSelectModule } from '@angular/material/select';
import { BillingProduct, formatMoney, parseProductPrice } from '../../models/billing-product.type';
import { Membership } from '../../models/membership.type';
import {
  BillItem,
  groupBillItem,
  isBillDueDate,
  todayDateInputValue,
} from '../../models/patient-bill.type';
import { FirebaseAuthService } from '../../services/auth/firebase-auth.service';
import { MembershipService } from '../../services/auth/membership.service';
import {
  CalendarEventService,
  doctorOptionLabel,
} from '../../services/database/calendar-event.service';
import { BillingProductService } from '../../services/database/billing-product.service';
import { PatientBillService } from '../../services/database/patient-bill.service';

export interface BillDialogData {
  patientId: string;
  patientName: string;
}

@Component({
  selector: 'app-bill-dialog',
  templateUrl: './bill-dialog.component.html',
  styleUrl: './bill-dialog.component.css',
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [
    FormsModule,
    MatDialogModule,
    MatButtonModule,
    MatFormFieldModule,
    MatIconModule,
    MatInputModule,
    MatSelectModule,
  ],
})
export class BillDialogComponent {
  readonly data = inject<BillDialogData>(MAT_DIALOG_DATA);
  readonly dialogRef = inject(MatDialogRef<BillDialogComponent, string | undefined>);
  private auth = inject(FirebaseAuthService);
  private membership = inject(MembershipService);
  private calendar = inject(CalendarEventService);
  private bills = inject(PatientBillService);
  private catalog = inject(BillingProductService);

  readonly doctorLabel = doctorOptionLabel;
  readonly formatMoney = formatMoney;
  readonly picksDoctor = this.membership.isSelectedBusinessSecretary();
  readonly dueDate = signal(todayDateInputValue());
  readonly products = signal<BillingProduct[]>([]);
  readonly productId = signal('');
  readonly unitPrice = signal('');
  readonly items = signal<BillItem[]>([]);
  readonly doctors = signal<Membership[]>([]);
  readonly doctorUid = signal('');
  readonly loadingDoctors = signal(this.picksDoctor);
  readonly loadingProducts = signal(true);
  readonly saving = signal(false);
  readonly error = signal('');

  readonly selectedDoctor = computed(
    () => this.doctors().find((doctor) => doctor.uid === this.doctorUid()) ?? null
  );

  readonly selectedProduct = computed(
    () => this.products().find((product) => product.id === this.productId()) ?? null
  );

  readonly priceLocked = computed(() => this.selectedProduct()?.priceEditable !== true);

  constructor() {
    void this.loadDoctors();
    void this.loadProducts();
  }

  selectProduct(productId: string): void {
    this.productId.set(productId);
    const product = this.products().find((item) => item.id === productId);
    this.unitPrice.set(product ? String(product.price) : '');
    this.error.set('');
  }

  addItem(): void {
    const product = this.selectedProduct();
    if (!product) {
      this.error.set('Seleccione un producto.');
      return;
    }
    const unitPrice = product.priceEditable
      ? parseProductPrice(this.unitPrice())
      : product.price;
    if (unitPrice == null) {
      this.error.set('El precio no es válido.');
      return;
    }
    const next = groupBillItem(this.items(), {
      productId: product.id,
      name: product.name,
      unitPrice,
    });
    if (next === this.items()) {
      return;
    }
    this.items.set(next);
    this.error.set('');
  }

  selectedDoctorLabel(): string {
    const doctor = this.selectedDoctor();
    return doctor ? doctorOptionLabel(doctor) : '';
  }

  setQuantity(index: number, raw: string | number): void {
    const quantity = Math.floor(Number(raw));
    if (!Number.isFinite(quantity) || quantity < 1) {
      return;
    }
    this.items.update((items) =>
      items.map((item, i) => (i === index ? { ...item, quantity } : item))
    );
  }

  removeItem(index: number): void {
    this.items.update((items) => items.filter((_, i) => i !== index));
  }

  async save(): Promise<void> {
    const doctor = this.selectedDoctor();
    if (!doctor) {
      this.error.set('Debe seleccionar un doctor.');
      return;
    }
    if (!isBillDueDate(this.dueDate())) {
      this.error.set('La fecha de vencimiento no es válida.');
      return;
    }
    if (this.items().length === 0) {
      this.error.set('Agregue al menos un producto.');
      return;
    }
    this.saving.set(true);
    this.error.set('');
    try {
      const id = await this.bills.createBill({
        patientId: this.data.patientId,
        patientName: this.data.patientName,
        doctorUid: doctor.uid,
        doctorName: doctorOptionLabel(doctor),
        dueDate: this.dueDate(),
        items: this.items(),
      });
      this.dialogRef.close(id);
    } catch (err) {
      console.error('[BillDialog] save failed', err);
      this.error.set(err instanceof Error ? err.message : 'No se pudo guardar el cobro.');
      this.saving.set(false);
    }
  }

  private async loadDoctors(): Promise<void> {
    try {
      if (!this.picksDoctor) {
        const uid = this.auth.uniqueId();
        const self = this.membership.selectedMembership();
        if (!uid || !self || self.uid !== uid) {
          this.error.set('No se pudo identificar al doctor.');
          return;
        }
        this.doctors.set([self]);
        this.doctorUid.set(uid);
        return;
      }
      const doctors = await this.calendar.listActiveDoctors();
      this.doctors.set(doctors);
      if (doctors.length === 1) {
        this.doctorUid.set(doctors[0].uid);
      }
      if (doctors.length === 0) {
        this.error.set('No hay doctores activos en este consultorio.');
      }
    } catch (err) {
      console.error('[BillDialog] doctors failed', err);
      this.error.set('No se pudieron cargar los doctores.');
    } finally {
      this.loadingDoctors.set(false);
    }
  }

  private async loadProducts(): Promise<void> {
    try {
      const products = await this.catalog.listProducts();
      this.products.set(products);
      if (products.length === 0) {
        this.error.set('No hay productos. El propietario puede agregarlos en Consultorio.');
      }
    } catch (err) {
      console.error('[BillDialog] products failed', err);
      this.error.set('No se pudieron cargar los productos.');
    } finally {
      this.loadingProducts.set(false);
    }
  }
}
